import type {
  Iso3166TwoDigitCountryCode,
  OnboardingRequirementCollectDocument,
  OnboardingSessionConfig,
} from '@onefootprint/request-types';
import { createContext, useContext, useReducer } from 'react';
import type { DeviceInfo } from '@/idv2/utils/get-device';
import type {
  CameraErrorCause,
  IdDocImageErrorType,
  IdDocImageSide,
  IdDocOutcome,
  ReceivedImagePayload,
  SupportedIdDocType,
} from '../../types';
import { isDenied, isGranted, isMobileKind, isPrompt } from '../../utils/capture';

export type IdDocStep =
  | 'countryAndType'
  | 'mobileRequestCameraAccess'
  | 'mobileCameraAccessDenied'
  | 'mobileCameraErrored'
  | 'consent'
  | 'desktopFrontImage'
  | 'mobileFrontImageCapture'
  | 'mobileFrontPhotoFallback'
  | 'mobileFrontImageRetry'
  | 'desktopFrontImageRetry'
  | 'desktopBackImage'
  | 'mobileBackImageCapture'
  | 'mobileBackPhotoFallback'
  | 'desktopBackImageRetry'
  | 'mobileBackImageRetry'
  | 'mobileSelfieImage'
  | 'mobileSelfieFallback'
  | 'desktopSelfieImage'
  | 'mobileSelfieImageRetry'
  | 'desktopSelfieImageRetry'
  | 'mobileProcessing'
  | 'desktopProcessing'
  | 'complete'
  | 'failure';

export type IdDocContext = {
  authToken: string;
  cameraPermissionState: PermissionState;
  device: DeviceInfo;
  requirement: OnboardingRequirementCollectDocument;
  isConsentMissing: boolean;
  image?: ReceivedImagePayload;
  currSide?: IdDocImageSide;
  idDoc: {
    type?: `${SupportedIdDocType}`;
    country?: Iso3166TwoDigitCountryCode;
  };
  id?: string;
  allowPdf: boolean;
  errors?: IdDocImageErrorType[];
  sandboxOutcome?: IdDocOutcome;
  hasBadConnectivity?: boolean;
  onboardingConfig: OnboardingSessionConfig;
  cameraErrorCause?: CameraErrorCause;
};

export type IdDocState = {
  value: IdDocStep;
  context: IdDocContext;
};

export type IdDocAction =
  | { type: 'cameraAccessDenied'; payload: { status: PermissionState } }
  | { type: 'cameraAccessGranted'; payload: { status: PermissionState } }
  | { type: 'cameraErrored'; payload: { cause: CameraErrorCause } }
  | { type: 'cameraStuck' }
  | { type: 'consentReceived' }
  | { type: 'navigatedToCountryDoc' }
  | { type: 'navigatedToPrev' }
  | { type: 'processingErrored'; payload: { errors: IdDocImageErrorType[] } }
  | {
      type: 'receivedCountryAndType';
      payload: { type?: `${SupportedIdDocType}`; country?: Iso3166TwoDigitCountryCode; id: string };
    }
  | { type: 'receivedImage'; payload: ReceivedImagePayload }
  | { type: 'retryLimitExceeded' }
  | { type: 'startImageCapture' }
  | { type: 'uploadErrored'; payload: { errors: IdDocImageErrorType[] } }
  | { type: 'processingSucceeded'; payload: { nextSideToCollect?: string } };

const idDocReducer = (state: IdDocState, action: IdDocAction): IdDocState => {
  const { value: step, context: ctx } = state;
  const isMobile = isMobileKind(ctx.device.type);

  switch (action.type) {
    case 'receivedCountryAndType': {
      if (step !== 'countryAndType') return state;
      const newCtx: IdDocContext = {
        ...ctx,
        idDoc: { type: action.payload.type, country: action.payload.country },
        id: action.payload.id,
        currSide: 'front',
      };
      if (isMobile && !newCtx.isConsentMissing && isPrompt(newCtx.cameraPermissionState)) {
        return { value: 'mobileRequestCameraAccess', context: newCtx };
      }
      if (isMobile && !newCtx.isConsentMissing && isDenied(newCtx.cameraPermissionState)) {
        return { value: 'mobileCameraAccessDenied', context: newCtx };
      }
      if (isMobile && !newCtx.isConsentMissing && isGranted(newCtx.cameraPermissionState)) {
        return { value: 'mobileFrontImageCapture', context: newCtx };
      }
      if (!isMobile && !newCtx.isConsentMissing) {
        return { value: 'desktopFrontImage', context: newCtx };
      }
      return { value: 'consent', context: newCtx };
    }

    case 'consentReceived': {
      if (step !== 'consent') return state;
      const newCtx: IdDocContext = { ...ctx, isConsentMissing: false };
      if (isMobile && isPrompt(newCtx.cameraPermissionState)) {
        return { value: 'mobileRequestCameraAccess', context: newCtx };
      }
      if (isMobile && isDenied(newCtx.cameraPermissionState)) {
        return { value: 'mobileCameraAccessDenied', context: newCtx };
      }
      if (isMobile && newCtx.id) {
        return { value: 'mobileFrontImageCapture', context: newCtx };
      }
      if (!isMobile && newCtx.id) {
        return { value: 'desktopFrontImage', context: newCtx };
      }
      return state;
    }

    case 'navigatedToPrev': {
      if (step === 'mobileRequestCameraAccess') return { value: 'countryAndType', context: ctx };
      if (step === 'mobileCameraAccessDenied') return { value: 'mobileRequestCameraAccess', context: ctx };
      if (step === 'mobileCameraErrored') return { value: 'countryAndType', context: ctx };
      if (step === 'consent') return { value: 'countryAndType', context: ctx };
      if (step === 'mobileFrontImageCapture') return { value: 'countryAndType', context: ctx };
      if (step === 'mobileFrontPhotoFallback') return { value: 'countryAndType', context: ctx };
      if (step === 'mobileBackImageCapture') return { value: 'countryAndType', context: ctx };
      if (step === 'mobileBackPhotoFallback') return { value: 'countryAndType', context: ctx };
      if (step === 'mobileSelfieImage') return { value: 'countryAndType', context: ctx };
      if (step === 'mobileSelfieFallback') return { value: 'countryAndType', context: ctx };
      return state;
    }

    case 'navigatedToCountryDoc': {
      const newCtx: IdDocContext = { ...ctx, errors: [], image: undefined };
      return { value: 'countryAndType', context: newCtx };
    }

    case 'cameraAccessGranted': {
      if (step !== 'mobileRequestCameraAccess') return state;
      const newCtx: IdDocContext = { ...ctx, cameraPermissionState: action.payload.status };
      if (isMobile && !newCtx.isConsentMissing && isGranted(action.payload.status)) {
        return { value: 'mobileFrontImageCapture', context: newCtx };
      }
      return state;
    }

    case 'cameraAccessDenied': {
      const newCtx: IdDocContext = { ...ctx, cameraPermissionState: action.payload.status };
      return { value: 'mobileCameraAccessDenied', context: newCtx };
    }

    case 'cameraErrored': {
      const newCtx: IdDocContext = { ...ctx, cameraErrorCause: action.payload.cause };
      return { value: 'mobileCameraErrored', context: newCtx };
    }

    case 'cameraStuck': {
      if (step === 'mobileFrontImageCapture') return { value: 'mobileFrontPhotoFallback', context: ctx };
      if (step === 'mobileBackImageCapture') return { value: 'mobileBackPhotoFallback', context: ctx };
      if (step === 'mobileSelfieImage') return { value: 'mobileSelfieFallback', context: ctx };
      return state;
    }

    case 'receivedImage': {
      const newCtx: IdDocContext = {
        ...ctx,
        image: {
          imageFile: action.payload.imageFile,
          captureKind: action.payload.captureKind,
          extraCompressed: action.payload.extraCompressed,
          forcedUpload: action.payload.forcedUpload,
          videoFile: action.payload.videoFile,
        },
      };
      if (
        step === 'mobileFrontImageCapture' ||
        step === 'mobileFrontPhotoFallback' ||
        step === 'mobileFrontImageRetry' ||
        step === 'mobileBackImageCapture' ||
        step === 'mobileBackPhotoFallback' ||
        step === 'mobileBackImageRetry' ||
        step === 'mobileSelfieImage' ||
        step === 'mobileSelfieFallback' ||
        step === 'mobileSelfieImageRetry'
      ) {
        return { value: 'mobileProcessing', context: newCtx };
      }
      if (
        step === 'desktopFrontImage' ||
        step === 'desktopFrontImageRetry' ||
        step === 'desktopBackImage' ||
        step === 'desktopBackImageRetry' ||
        step === 'desktopSelfieImage' ||
        step === 'desktopSelfieImageRetry'
      ) {
        return { value: 'desktopProcessing', context: newCtx };
      }
      return state;
    }

    case 'uploadErrored': {
      const newCtx: IdDocContext = { ...ctx, errors: action.payload.errors };
      if (step === 'desktopFrontImage') return { value: 'desktopFrontImageRetry', context: newCtx };
      if (step === 'desktopFrontImageRetry') return { value: 'desktopFrontImageRetry', context: newCtx };
      if (step === 'desktopBackImage') return { value: 'desktopBackImageRetry', context: newCtx };
      if (step === 'desktopBackImageRetry') return { value: 'desktopBackImageRetry', context: newCtx };
      return state;
    }

    case 'startImageCapture': {
      if (step === 'mobileFrontImageRetry') return { value: 'mobileFrontImageCapture', context: ctx };
      if (step === 'mobileBackImageRetry') return { value: 'mobileBackImageCapture', context: ctx };
      if (step === 'mobileSelfieImageRetry') return { value: 'mobileSelfieImage', context: ctx };
      if (step === 'desktopSelfieImageRetry') return { value: 'desktopSelfieImage', context: ctx };
      return state;
    }

    case 'processingSucceeded': {
      if (step !== 'mobileProcessing' && step !== 'desktopProcessing') return state;
      const nextSide = action.payload.nextSideToCollect;
      const newCtx: IdDocContext = {
        ...ctx,
        currSide: nextSide ? (nextSide as IdDocImageSide) : ctx.currSide,
        errors: [],
      };
      if (isMobile) {
        if (nextSide === 'front') return { value: 'mobileFrontImageCapture', context: newCtx };
        if (nextSide === 'back') return { value: 'mobileBackImageCapture', context: newCtx };
        if (nextSide === 'selfie') return { value: 'mobileSelfieImage', context: newCtx };
      } else {
        if (nextSide === 'front') return { value: 'desktopFrontImage', context: newCtx };
        if (nextSide === 'back') return { value: 'desktopBackImage', context: newCtx };
        if (nextSide === 'selfie') return { value: 'desktopSelfieImage', context: newCtx };
      }
      return { value: 'complete', context: newCtx };
    }

    case 'processingErrored': {
      if (step !== 'mobileProcessing' && step !== 'desktopProcessing') return state;
      const hasBadConnectivity =
        action.payload.errors.some(e => e.errorType === 'network_error') || ctx.hasBadConnectivity;
      const newCtx: IdDocContext = {
        ...ctx,
        errors: action.payload.errors,
        image: undefined,
        hasBadConnectivity,
      };
      if (isMobile) {
        if (ctx.currSide === 'front') return { value: 'mobileFrontImageRetry', context: newCtx };
        if (ctx.currSide === 'back') return { value: 'mobileBackImageRetry', context: newCtx };
        if (ctx.currSide === 'selfie') return { value: 'mobileSelfieImageRetry', context: newCtx };
      } else {
        if (ctx.currSide === 'front') return { value: 'desktopFrontImageRetry', context: newCtx };
        if (ctx.currSide === 'back') return { value: 'desktopBackImageRetry', context: newCtx };
        if (ctx.currSide === 'selfie') return { value: 'desktopSelfieImageRetry', context: newCtx };
      }
      return state;
    }

    case 'retryLimitExceeded': {
      return { value: 'failure', context: ctx };
    }

    default:
      return state;
  }
};

type IdDocContextValue = [IdDocState, React.Dispatch<IdDocAction>];

const IdDocCtx = createContext<IdDocContextValue | undefined>(undefined);

type IdDocProviderProps = {
  initialContext: IdDocContext;
  initStep?: IdDocStep;
  children: React.ReactNode;
};

const IdDocProvider = ({ initialContext, initStep, children }: IdDocProviderProps) => {
  const [state, dispatch] = useReducer(idDocReducer, {
    value: initStep ?? 'countryAndType',
    context: initialContext,
  });

  return <IdDocCtx.Provider value={[state, dispatch]}>{children}</IdDocCtx.Provider>;
};

const useIdDocMachine = (): IdDocContextValue => {
  const ctx = useContext(IdDocCtx);
  if (!ctx) {
    throw new Error('useIdDocMachine must be used inside IdDocProvider');
  }
  return ctx;
};

export { IdDocProvider, useIdDocMachine };
